/**
 * Web push: company-level delivery, user-level control.
 *
 * This hook owns ONLY the user-level half of that contract:
 *   - browser permission (inherently per-device)
 *   - this device's subscription row
 *   - this user's per-kind mute switches and personal pause window
 *
 * Nothing here can silence a teammate. Company-level fan-out and quiet hours
 * live in the database (`enqueue_push_for_notification` / `drain_push_queue`).
 *
 * Registration is deliberately guarded: never in dev, never inside the Lovable
 * preview iframe, and never when `?sw=off` is present. The worker at
 * /push-sw.js handles push + notificationclick only — no app-shell caching.
 */
import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { trackActivity } from '@/lib/activityTracking';

const VAPID_PUBLIC_KEY =
  'BNtskM-MC1HLI5Wo5TBj4IJLP9K7tEcQTaDT30In2v_o6HrsplW-E01pH-J8HFWfJBR1Kww-cf2Brlzi39gmTsk';

const SW_PATH = '/push-sw.js';

export type PushKind =
  | 'buyer_interest'
  | 'saved_search_matches'
  | 'price_drop'
  | 'supplier_restock';

export interface PushPreferences {
  buyer_interest: boolean;
  saved_search_matches: boolean;
  price_drop: boolean;
  supplier_restock: boolean;
  paused_until: string | null;
}

const DEFAULT_PREFS: PushPreferences = {
  buyer_interest: true,
  saved_search_matches: true,
  price_drop: true,
  supplier_restock: true,
  paused_until: null,
};

export const PUSH_PREFS_KEY = ['push-preferences'];

/**
 * Push is pointless in preview iframes and dev — and registering there breaks HMR.
 * Exported for unit tests: getting this wrong either fires push inside the editor
 * preview or silently never registers in production.
 */
export function environmentAllowsPush(): boolean {
  if (typeof window === 'undefined') return false;
  if (!import.meta.env.PROD) return false;
  if (window.self !== window.top) return false;
  if (new URL(window.location.href).searchParams.get('sw') === 'off') return false;
  const host = window.location.hostname;
  if (host.startsWith('id-preview--') || host.startsWith('preview--')) return false;
  if (host === 'lovableproject.com' || host.endsWith('.lovableproject.com')) return false;
  if (host === 'lovableproject-dev.com' || host.endsWith('.lovableproject-dev.com')) return false;
  if (host === 'beta.lovable.dev' || host.endsWith('.beta.lovable.dev')) return false;
  return true;
}

export function pushIsSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function urlBase64ToBuffer(base64: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i);
  return buffer;
}

/**
 * @param surface Optional label for where the opt-in was asked (e.g. "watchlist"),
 *                recorded on the permission events so we can see which contextual
 *                ask converts.
 */
export function usePushNotifications(surface?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [permission, setPermission] = useState<NotificationPermission>(
    pushIsSupported() ? Notification.permission : 'denied',
  );
  const [deviceEnabled, setDeviceEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  const supported = pushIsSupported();
  const available = supported && environmentAllowsPush();

  // Reflect whether THIS device already has a live subscription.
  useEffect(() => {
    if (!available || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
        const sub = await reg?.pushManager.getSubscription();
        if (!cancelled) setDeviceEnabled(!!sub);
      } catch {
        if (!cancelled) setDeviceEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [available, user]);

  const prefsQuery = useQuery({
    queryKey: [...PUSH_PREFS_KEY, user?.id],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async (): Promise<PushPreferences> => {
      const { data, error } = await supabase
        .from('user_push_preferences')
        .select('buyer_interest, saved_search_matches, price_drop, supplier_restock, paused_until')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data ? { ...DEFAULT_PREFS, ...data } : DEFAULT_PREFS;
    },
  });

  const savePrefs = useMutation({
    mutationFn: async (patch: Partial<PushPreferences>) => {
      /* A partial upsert has to merge onto the row we loaded. If that load failed,
         prefsQuery.data is undefined and we would spread DEFAULT_PREFS ("all on")
         over the buyer's real mutes — silently destroying stored investment.
         Refuse the write instead, and tell the caller why. */
      if (prefsQuery.isError || !prefsQuery.data) {
        throw new Error('PREFS_NOT_LOADED');
      }
      const { error } = await supabase
        .from('user_push_preferences')
        .upsert(
          { user_id: user!.id, ...prefsQuery.data, ...patch },
          { onConflict: 'user_id' },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PUSH_PREFS_KEY });
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'PREFS_NOT_LOADED') {
        toast.error("We couldn't read your current alert settings", {
          description:
            "So we didn't save this change — that avoids overwriting the alert types you've already muted. Reload and try again.",
        });
        return;
      }
      toast.error('Could not save your push settings', {
        description: 'Try again in a moment.',
      });
    },
  });

  /** Contextual opt-in. Never call this on page load — only from a user action. */
  const enable = useCallback(async (): Promise<boolean> => {
    if (!available || !user) return false;
    setBusy(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') {
        trackActivity('push_permission_denied', { surface: surface ?? 'settings', result });
        if (result === 'denied') {
          toast.info('Notifications are blocked for this site', {
            description: 'Re-enable them in your browser settings, then try again.',
          });
        }
        return false;
      }
      trackActivity('push_permission_granted', { surface: surface ?? 'settings' });

      const reg = await navigator.serviceWorker.register(SW_PATH);
      await navigator.serviceWorker.ready;

      const existing = await reg.pushManager.getSubscription();
      const sub =
        existing ??
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToBuffer(VAPID_PUBLIC_KEY),
        }));

      const json = sub.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false;

      const { data: profile } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .maybeSingle();

      const { error } = await supabase.from('push_subscriptions').upsert(
        {
          user_id: user.id,
          company_id: profile?.company_id ?? null,
          endpoint: json.endpoint,
          p256dh: json.keys.p256dh,
          auth: json.keys.auth,
          user_agent: navigator.userAgent.slice(0, 300),
          last_seen_at: new Date().toISOString(),
          failed_at: null,
        },
        { onConflict: 'endpoint' },
      );
      if (error) throw error;

      setDeviceEnabled(true);
      toast.success('Push notifications are on for this device', {
        description: 'We only ping you for live buyer interest and fresh matches.',
      });
      return true;
    } catch (err) {
      console.error('[push] enable failed', err);
      toast.error('Could not turn on push notifications', {
        description: 'Your browser may have blocked the request.',
      });
      return false;
    } finally {
      setBusy(false);
    }
  }, [available, user, surface]);

  /** Turns push off for THIS device only. Teammates are untouched. */
  const disable = useCallback(async (): Promise<void> => {
    if (!supported || !user) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint);
      }
      setDeviceEnabled(false);
      toast.success('Push is off for this device', {
        description: 'Your bell and email alerts are unchanged, and your teammates keep theirs.',
      });
    } catch (err) {
      console.error('[push] disable failed', err);
    } finally {
      setBusy(false);
    }
  }, [supported, user]);

  const pauseFor = useCallback(
    (hours: number) => {
      const until = new Date(Date.now() + hours * 3600_000).toISOString();
      savePrefs.mutate({ paused_until: until });
    },
    [savePrefs],
  );

  const resume = useCallback(() => savePrefs.mutate({ paused_until: null }), [savePrefs]);

  const prefs = prefsQuery.data ?? DEFAULT_PREFS;
  const pausedUntil = prefs.paused_until ? new Date(prefs.paused_until) : null;
  const isPaused = !!pausedUntil && pausedUntil.getTime() > Date.now();

  return {
    supported,
    available,
    permission,
    deviceEnabled,
    busy,
    prefs,
    prefsLoading: prefsQuery.isLoading,
    prefsError: prefsQuery.isError,
    refetchPrefs: prefsQuery.refetch,
    isPaused,
    pausedUntil,
    enable,
    disable,
    setKind: (kind: PushKind, value: boolean) => savePrefs.mutate({ [kind]: value }),
    pauseFor,
    resume,
  };
}
